import { MerchantFeature } from "@redotech/crdb-enums/internal-ops/merchant-feature";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  MAX_MERCHANT_FEATURE_PAUSE_DAYS,
  MerchantPausedFeature,
  PauseStatus,
  pauseStatus,
} from "@redotech/redo-merchant-pause-common/paused-feature";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoTimePicker,
  TimeOfDay,
} from "@redotech/redo-web/arbiter-components/time/redo-time-picker";
import {
  dateToPlainDate,
  plainDateToDate,
} from "@redotech/redo-web/arbiter-components/time/time-zone-conversion";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import {
  CoveragePauseRequest,
  coveragePauseWindow,
  defaultCoveragePauseDraft,
  latestPauseEndDate,
} from "./coverage-pause-window";

const DATE_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const PAST_PAUSES_SHOWN = 5;

function merchantPausedFeaturesQueryKey(teamId: string) {
  return ["merchantPausedFeatures", teamId, MerchantFeature.Coverage] as const;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export const CoveragePauseCard = memo(function CoveragePauseCard({
  teamId,
}: {
  teamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => merchantPausedFeaturesQueryKey(teamId),
    [teamId],
  );
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [endModalOpen, setEndModalOpen] = useState(false);

  const { data, isPending, error } = useQuery({
    queryKey,
    queryFn: () =>
      rpcClient.getMerchantPausedFeatures({
        teamId,
        feature: MerchantFeature.Coverage,
      }),
  });

  const now = new Date();
  const pauses = data?.pauses ?? [];
  // Windows cannot overlap, so at most one covers `now`; the rest are queued
  // behind it and each can be cancelled on its own. The query returns newest
  // first for the history, so re-sort ascending — otherwise the furthest-out
  // window would be shown as the current one.
  const live = pauses
    .filter((pause) => pauseStatus(pause, now) !== PauseStatus.ENDED)
    .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
  const current = live[0];
  const currentStatus = current ? pauseStatus(current, now) : null;
  const upcoming = live.slice(1);
  const history = pauses.filter(
    (pause) => pauseStatus(pause, now) === PauseStatus.ENDED,
  );

  const endMutation = useMutation({
    mutationFn: (pauseId: MerchantPausedFeature["id"]) =>
      rpcClient.endMerchantFeaturePause({ teamId, pauseId }),
    onSuccess: ({ result }) => {
      toast(
        result === "ended" ? "Coverage resumed" : "Scheduled pause cancelled",
        { variant: "success" },
      );
      setEndModalOpen(false);
    },
    onError: (err) => {
      toast(errorMessage(err), { variant: "error" });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <Flex dir="column" gap="lg">
      {error ? (
        <Text fontSize="sm" textColor="error">
          Failed to load coverage pauses: {errorMessage(error)}
        </Text>
      ) : null}
      <Flex align="center" gap="lg" justify="space-between">
        {isPending ? (
          <Text fontSize="sm" textColor="secondary">
            Loading…
          </Text>
        ) : error ? (
          // A failed load leaves `current` empty, which must not be read as
          // "no pause is running" — the pause state is simply unknown.
          <Text fontSize="sm" textColor="secondary">
            Coverage pause state is unavailable.
          </Text>
        ) : current && currentStatus ? (
          <CurrentPauseSummary pause={current} status={currentStatus} />
        ) : (
          <Text fontSize="sm" textColor="secondary">
            Coverage is not paused.
          </Text>
        )}
        <Flex align="center" gap="sm">
          {current ? (
            <RedoButton
              hierarchy="secondary"
              onClick={() => setEndModalOpen(true)}
              size="sm"
              text={
                currentStatus === PauseStatus.ACTIVE
                  ? "Resume coverage now"
                  : "Cancel scheduled pause"
              }
              theme="normal"
            />
          ) : null}
          <RedoButton
            disabled={isPending || !!error}
            hierarchy="primary"
            onClick={() => setPauseModalOpen(true)}
            size="sm"
            text={current ? "Schedule another" : "Pause coverage"}
            theme="normal"
          />
        </Flex>
      </Flex>
      {upcoming.length > 0 ? (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Scheduled next
          </Text>
          {upcoming.map((pause) => (
            <UpcomingPause key={pause.id} pause={pause} teamId={teamId} />
          ))}
        </Flex>
      ) : null}
      {history.length > 0 ? <PauseHistory pauses={history} /> : null}
      {pauseModalOpen ? (
        <PauseCoverageModal
          onClose={() => setPauseModalOpen(false)}
          teamId={teamId}
        />
      ) : null}
      {endModalOpen && current ? (
        <RedoModal
          isOpen
          modalSize="sm"
          onModalCloseRequested={() => setEndModalOpen(false)}
          primaryButton={{
            text:
              currentStatus === PauseStatus.ACTIVE
                ? "Resume coverage"
                : "Cancel pause",
            onClick: () => endMutation.mutate(current.id),
            pending: endMutation.isPending,
          }}
          secondaryButton={{
            text: "Keep pause",
            onClick: () => setEndModalOpen(false),
          }}
          subtitle={
            currentStatus === PauseStatus.ACTIVE
              ? "Coverage turns back on within a minute on the storefront, checkout and public API. A shopper who has not loaded the page for a while can see the paused state once more before their cached widget payload refreshes. The pause stays in the history."
              : "The scheduled pause is removed and coverage stays on."
          }
          theme="warn"
          title={
            currentStatus === PauseStatus.ACTIVE
              ? "Resume coverage now?"
              : "Cancel the scheduled pause?"
          }
        />
      ) : null}
    </Flex>
  );
});

const CurrentPauseSummary = memo(function CurrentPauseSummary({
  pause,
  status,
}: {
  pause: MerchantPausedFeature;
  status: PauseStatus;
}) {
  const active = status === PauseStatus.ACTIVE;
  return (
    <Flex dir="column" gap="xs">
      <Flex align="center" gap="sm">
        <Flex shrink={0}>
          <RedoBadge
            color={active ? "warning" : "blue"}
            size="sm"
            text={active ? "Paused" : "Scheduled"}
          />
        </Flex>
        <Text fontSize="sm" fontWeight="medium">
          {active
            ? `Coverage resumes ${DATE_TIME_FORMAT.format(pause.endsAt)}`
            : `Pauses ${DATE_TIME_FORMAT.format(pause.startsAt)} until ${DATE_TIME_FORMAT.format(pause.endsAt)}`}
        </Text>
      </Flex>
      {pause.reason ? (
        <Text fontSize="sm" textColor="secondary">
          {pause.reason}
        </Text>
      ) : null}
      <Text fontSize="xs" textColor="tertiary">
        Set by {pause.createdBy} on {DATE_TIME_FORMAT.format(pause.createdAt)}
      </Text>
    </Flex>
  );
});

/** A window queued behind the one covering now; cancellable on its own. */
const UpcomingPause = memo(function UpcomingPause({
  pause,
  teamId,
}: {
  pause: MerchantPausedFeature;
  teamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: () =>
      rpcClient.endMerchantFeaturePause({ teamId, pauseId: pause.id }),
    onSuccess: () => {
      toast("Scheduled pause cancelled", { variant: "success" });
      void queryClient.invalidateQueries({
        queryKey: merchantPausedFeaturesQueryKey(teamId),
      });
    },
    onError: (err) => toast(errorMessage(err), { variant: "error" }),
  });

  return (
    <Flex align="center" gap="lg" justify="space-between">
      <Flex dir="column" gap="xxs">
        <Text fontSize="sm">
          {DATE_TIME_FORMAT.format(pause.startsAt)} –{" "}
          {DATE_TIME_FORMAT.format(pause.endsAt)}
        </Text>
        <Text fontSize="xs" textColor="tertiary">
          {pause.reason ? `${pause.reason} · ` : ""}by {pause.createdBy}
        </Text>
      </Flex>
      <RedoButton
        hierarchy="tertiary"
        onClick={() => cancel.mutate()}
        pending={cancel.isPending}
        size="sm"
        text="Cancel"
        theme="normal"
      />
    </Flex>
  );
});

const PauseHistory = memo(function PauseHistory({
  pauses,
}: {
  pauses: MerchantPausedFeature[];
}) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? pauses : pauses.slice(0, PAST_PAUSES_SHOWN);
  return (
    <Flex dir="column" gap="sm">
      <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
        Past pauses
      </Text>
      {shown.map((pause) => (
        <Flex dir="column" gap="xxs" key={pause.id}>
          <Text fontSize="sm">
            {DATE_TIME_FORMAT.format(pause.startsAt)} –{" "}
            {DATE_TIME_FORMAT.format(pause.endsAt)}
          </Text>
          <Text fontSize="xs" textColor="tertiary">
            {pause.reason ? `${pause.reason} · ` : ""}by {pause.createdBy}
          </Text>
        </Flex>
      ))}
      {pauses.length > PAST_PAUSES_SHOWN ? (
        <Flex>
          <RedoButton
            hierarchy="tertiary"
            onClick={() => setShowAll((value) => !value)}
            size="sm"
            text={
              showAll
                ? "See less"
                : `See more (${pauses.length - PAST_PAUSES_SHOWN})`
            }
            theme="normal"
          />
        </Flex>
      ) : null}
    </Flex>
  );
});

const PauseCoverageModal = memo(function PauseCoverageModal({
  teamId,
  onClose,
}: {
  teamId: string;
  onClose(): void;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();
  const timeZone = useMemo(() => Temporal.Now.timeZoneId(), []);
  const [defaults] = useState(() =>
    defaultCoveragePauseDraft(new Date(), timeZone),
  );
  const today = dateToPlainDate(new Date(), timeZone);
  const [startImmediately, setStartImmediately] = useState(
    defaults.startImmediately,
  );
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [startTime, setStartTime] = useState<TimeOfDay | null>(
    defaults.startTime,
  );
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [endTime, setEndTime] = useState<TimeOfDay | null>(defaults.endTime);
  const [reason, setReason] = useState("");

  const effectiveStart =
    !startImmediately && startTime
      ? plainDateToDate(startDate, startTime, timeZone)
      : new Date();
  const latestEndDate = latestPauseEndDate(effectiveStart, timeZone);
  const changeStartDate = useHandler((date: Temporal.PlainDate) => {
    setStartDate(date);
    const startsAt = plainDateToDate(
      date,
      startTime ?? { hours: 0, minutes: 0 },
      timeZone,
    );
    const latest = latestPauseEndDate(startsAt, timeZone);
    if (
      Temporal.PlainDate.compare(endDate, date) < 0 ||
      Temporal.PlainDate.compare(endDate, latest) > 0
    ) {
      const nextDefaults = defaultCoveragePauseDraft(startsAt, timeZone);
      setEndDate(nextDefaults.endDate);
      setEndTime(nextDefaults.endTime);
    }
  });

  const draft = { startImmediately, startDate, startTime, endDate, endTime };
  const pauseWindow = coveragePauseWindow(draft, new Date(), timeZone);

  const pauseMutation = useMutation({
    mutationFn: (request: CoveragePauseRequest) =>
      rpcClient.pauseMerchantFeature({
        teamId,
        feature: MerchantFeature.Coverage,
        startsAt: request.startsAt,
        endsAt: request.endsAt,
        reason: reason.trim() || undefined,
      }),
    onSuccess: (_result, request) => {
      toast(request.startsAt ? "Pause scheduled" : "Coverage paused", {
        variant: "success",
      });
      onClose();
    },
    onError: (err) => {
      toast(errorMessage(err), { variant: "error" });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: merchantPausedFeaturesQueryKey(teamId),
      });
    },
  });

  return (
    <RedoModal
      isOpen
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Pause coverage",
        disabled: pauseWindow.error !== null,
        pending: pauseMutation.isPending,
        onClick: () => {
          const currentWindow = coveragePauseWindow(
            draft,
            new Date(),
            timeZone,
          );
          if (currentWindow.request) {
            pauseMutation.mutate(currentWindow.request);
          } else {
            toast(currentWindow.error, { variant: "error" });
          }
        },
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle={`Turns off returns, package protection and final sale coverage — shopper-purchased and merchant-paid alike — on the storefront, checkout and public API for up to ${MAX_MERCHANT_FEATURE_PAUSE_DAYS} days, within a minute of saving. A shopper who has not loaded the page for a while can see coverage once more before their cached widget payload refreshes. The merchant's settings are not changed. Redo-in-shipping rates that bundle coverage disappear for the pause, exactly as when coverage is disabled.`}
      title="Pause coverage"
    >
      <Flex dir="column" gap="lg">
        <Text fontSize="xs" textColor="secondary">
          All dates and times are in your time zone: {timeZone}.
        </Text>
        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="medium">
            Pause from
          </Text>
          <RedoCheckbox
            label="Start immediately"
            setValue={(value) => setStartImmediately(value === true)}
            size="sm"
            value={startImmediately}
          />
          {!startImmediately ? (
            <Flex align="center" aria-label="Pause start" gap="sm" role="group">
              <RedoSingleDatePicker
                date={startDate}
                minDate={today}
                setDate={changeStartDate}
                size="sm"
              />
              <RedoTimePicker
                setValue={setStartTime}
                size="sm"
                value={startTime}
              />
            </Flex>
          ) : null}
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="medium">
            Resume at
          </Text>
          <Text fontSize="xs" textColor="secondary">
            Coverage turns back on at this date and time.
          </Text>
          <Flex align="center" aria-label="Pause end" gap="sm" role="group">
            <RedoSingleDatePicker
              date={endDate}
              maxDate={latestEndDate}
              minDate={startImmediately ? today : startDate}
              setDate={setEndDate}
              size="sm"
            />
            <RedoTimePicker setValue={setEndTime} size="sm" value={endTime} />
          </Flex>
          {pauseWindow.error ? (
            <Text fontSize="xs" textColor="error">
              {pauseWindow.error}
            </Text>
          ) : null}
        </Flex>
        <RedoTextareaInputField
          label="Reason (optional)"
          placeholder="Shown in the pause history"
          setValue={setReason}
          value={reason}
        />
        {pauseWindow.request ? (
          <Text fontSize="sm" textColor="secondary">
            Coverage pauses{" "}
            {pauseWindow.request.startsAt
              ? DATE_TIME_FORMAT.format(pauseWindow.request.startsAt)
              : "as soon as you save"}{" "}
            and resumes {DATE_TIME_FORMAT.format(pauseWindow.request.endsAt)}.
          </Text>
        ) : null}
      </Flex>
    </RedoModal>
  );
});
